import { Fragment, useEffect, useState } from "react";
import axios from "axios";
import { CONTENT_ROUTES } from "../routes/content-routes";
import Header from "../components/header";
import Footer from "../components/footer";
import { NavLink } from "react-router-dom";
import { AppLoader } from "../components/app-loader";
import { WEB_ROUTES } from "../routes/web-routes";
import TeamItem from "../components/leadership-item";
import { Helmet } from "react-helmet";
import { useUtilities } from "../hooks/utilities";
import DivisionsScroller from "../components/divisions-scroller";

export default function About() {
  const { getMetaSnippet, getSeoTitle, getSeoDescription } = useUtilities();
  const [page, setPage] = useState({});
  const [loading, setLoading] = useState({});
  const [pageMedia, setPageMedia] = useState([]);
  const [leadership, setLeadership] = useState([]);
  const [leadershipMedia, setLeadershipMedia] = useState([]);
  const [hoveredMember, setHoveredMember] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [pageRes, leadershipRes] = await Promise.all([
          axios.get(CONTENT_ROUTES.POST_PAGE("/pages", "about-nant-power")),
          axios.get(
            CONTENT_ROUTES.LEADERSHIP(
              "?per_page=100&_embed&orderby=date&order=asc",
            ),
          ),
        ]);

        const pageResponse = pageRes.data?.length ? pageRes.data[0] : {};
        setPage(pageResponse);

        //Get page media
        const mediaRes = await axios.get(
          CONTENT_ROUTES.MEDIA_BY_PAGE(pageResponse?.id),
        );
        setPageMedia(mediaRes.data);

        setLeadership(
          Array.isArray(leadershipRes.data) ? leadershipRes.data : [],
        );
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <>
      <Helmet>
        <title>
          {getSeoTitle(page, page?.title?.rendered + ` - Nant Power`)}
        </title>
        <meta
          name="description"
          content={getSeoDescription(
            page,
            getMetaSnippet(page?.content?.rendered)
          )}
        />
      </Helmet>
      <Header />
      <main>
        <section className="relative">
          <div className="absolute z-10 bottom-0 left-0 w-full md:h-3/5 h-full bg-gradient-to-t from-black/60 to-transparent"></div>
          <div
            className="px-8 md:px-0 bg-gray-300 bg-no-repeat bg-top md:bg-center md:bg-cover pt-20 bg-[length:auto_100%]"
            style={{
              backgroundImage: `url(
              ${
                pageMedia?.find((media) => media?.id === page?.featured_media)
                  ?.source_url
              }
            )`,
            }}
          >
            <div className="container mx-auto text-white flex z-30">
              {/* Old title was here */}
            </div>
            <div className="container mx-auto md:flex content-center md:pt-60 pt-0 ">
              <div className="relative md:w-8/12 w-full text-white overflow-visible md:py-60 py-40">
                {/* Foreground content */}
                <div className="md:pt-52 absolute top-0 left-0 md:h-full md:pr-10 px-0 w-full z-10 text-left">
                  <h1 className="text-4xl font-medium mb-4">
                    {page?.title?.rendered}
                  </h1>
                  <div
                    className="md:block hidden"
                    dangerouslySetInnerHTML={{
                      __html: page?.content?.rendered
                        ?.replace(/<h2.*?>*?<\/h2>/g, "")
                        ?.replace(/<p>/g, "<p class='mb-4'>"),
                    }}
                  />
                </div>
              </div>
              <div className="relative md:pt-64 pt-40 md:pb-10 pb-40 md:w-4/12 w-full text-white md:pr-10 pr-0 text-2xl font-medium z-30">
                {/* Background image as a separate layer */}
                <div
                  className="absolute md:top-16 -top-60 left-0 md:h-full h-[620px] w-full z-30 bg-no-repeat md:bg-contain bg-[108%_auto] bg-center pointer-events-none"
                  style={{
                    backgroundImage: `url("/images/electric-bolt-outline.svg")`,
                  }}
                ></div>
                <h2
                  className="md:pt-64 pt-0 text-4xl font-medium absolute md:top-0 top-5 left-0 h-full md:px-10 px-0 w-full z-10 text-center"
                  dangerouslySetInnerHTML={{
                    __html: page?.content?.rendered
                      ?.replace(/<p.*?>*?<\/p>/g, "")
                      ?.replace(/<h2.*?>/g, "<h2>"),
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        <section className="relative bg-[#0070b9]">
          <div className="container mx-auto text-white px-8 md:px-0 md:py-20 py-16 md:flex md:gap-x-16 ">
            <div className="md:w-1/2 w-full">
              <div
                className="md:hidden block text-2xl font-medium "
                dangerouslySetInnerHTML={{
                  __html: page?.content?.rendered
                    ?.replace(/<h2.*?>*?<\/h2>/g, "")
                    ?.replace(/<p>/g, "<p class='mb-4'>"),
                }}
              />
              <div
                dangerouslySetInnerHTML={{
                  __html: page?.acf?.sub_section_left,
                }}
              />
            </div>
            <div className="md:w-1/2 w-full mt-10 md:mt-0">
              <div className="md:flex text-center h-[80%]">
                <div className="bg-[#58b947] md:w-1/2 w-full p-10 content-center text-base">
                  <div
                    className="mb-4 text-4xl"
                    dangerouslySetInnerHTML={{
                      __html: page?.acf?.mission_icon,
                    }}
                  />
                  <div
                    dangerouslySetInnerHTML={{
                      __html: page?.acf?.mission,
                    }}
                  />
                </div>
                <div className="bg-[#399d5b] md:w-1/2 w-full p-10 content-center text-base">
                  <div
                    className="mb-4 text-4xl"
                    dangerouslySetInnerHTML={{
                      __html: page?.acf?.vision_icon,
                    }}
                  />
                  <div
                    dangerouslySetInnerHTML={{
                      __html: page?.acf?.vision,
                    }}
                  />
                </div>
              </div>
              <div className="h-[20%] bg-gradient-to-b from-[#0065a6] to-transparent"></div>
            </div>
          </div>
        </section>

        <section className="relative bg-[#0071b9] pb-16">
          <div className="container mx-auto">
            <div className="md:w-full flex content-center md:px-0 px-8 pt-0 text-white">
              <div className="flex">
                <img
                  className="mr-5 h-12 -mt-2"
                  src="/images/electric-bolt-solid.svg"
                  alt="Nant Power Project Icon"
                />
                <div className="text-xl font-bold">Our Values</div>
              </div>
            </div>
          </div>
          <div className="container mx-auto md:flex text-white">
            {Array.from({ length: 5 }).map((_, i) => {
              const iconKey = `card_icon_${i + 1}`;
              const titleKey = `card_title_${i + 1}`;
              const bodyKey = `card_body_${i + 1}`;

              return (
                <div
                  key={i}
                  className="md:w-1/4 md:px-4 px-8 md:py-4 pt-4 pb-0 md:inline-block flex"
                >
                  <div
                    className="bg-[#58b947] py-10 text-center md:mx-auto mr-6 md:w-20 w-3/12 rounded-full mb-4 text-4xl"
                    dangerouslySetInnerHTML={{
                      __html: page?.acf?.values?.[iconKey],
                    }}
                  ></div>
                  <div className="md:w-full w-9/12 md:text-center text-left">
                    <div className="text-[#58b947] font-medium text-xl">
                      {page?.acf?.values?.[titleKey]}
                    </div>
                    <p>{page?.acf?.values?.[bodyKey]}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {leadership.length > 0 && (
          <section className="relative pb-16 bg-white border-b">
            <div className="container mx-auto">
              <div className="pt-16 md:px-0 px-8">
                <div className="flex mb-6">
                  <img
                    className="mr-5 h-12 -mt-2"
                    src="/images/electric-bolt-solid.svg"
                    alt="Nant Power Project Icon"
                  />
                  <span className="text-xl font-bold text-gray-500">
                    Leadership Team
                  </span>
                </div>

                {/* Board Leadership */}
                {
                  /* leadership.filter(member => member.acf?.leadership_category === 'Board').length > 0 && (
                  <div className="mb-12">
                    <h3 className="text-2xl font-semibold text-[#2aa837] mb-6">Board Leadership</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 list-none">
                      {leadership
                        .filter(member => member.acf?.leadership_category === 'Board')
                        .map((member, i) => ( */
                  <div className="md:grid xl:grid-rows-1 lg:grid-rows-2 lg:grid-flow-col lg:auto-cols-fr gap-6 md:space-y-0 space-y-6 list-none">
                    {leadership.map((member, i) => (
                      <Fragment key={i}>
                        <LeadershipItem
                          member={member}
                          onClick={setHoveredMember}
                        />
                      </Fragment>
                    ))}
                  </div>
                  /* ))}
                    </div>
                  </div>
                ) */
                }

                {/* Executive Leadership */}
                {/* {leadership.filter(
                  (member) => member.acf?.leadership_category === "Executive",
                ).length > 0 && (
                  <div>
                    <h3 className="text-2xl font-semibold text-[#2aa837] mb-6">
                      Executive Leadership
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 list-none">
                      {leadership
                        .filter(
                          (member) =>
                            member.acf?.leadership_category === "Executive",
                        )
                        .map((member, i) => (
                          <Fragment key={i}>
                            <LeadershipItem
                              member={member}
                              onClick={setHoveredMember}
                            />
                          </Fragment>
                        ))}
                    </div>
                  </div>
                )} */}
              </div>
            </div>
          </section>
        )}

        <section className="relative pb-16 bg-gradient-to-b from-transparent to-[#bad8ec]">
          <div className="container mx-auto">
            <div className="pt-16 md:px-0 px-8">
              <div className="flex mb-2">
                <img
                  className="mr-5 h-12 -mt-2"
                  src="/images/electric-bolt-solid.svg"
                  alt="Nant Power Project Icon"
                />
                <span className="text-xl font-bold text-gray-500">
                  Key Partners and Lenders
                </span>
              </div>
              <div>
                <DivisionsScroller />
              </div>
            </div>
          </div>
        </section>
      </main>

      {hoveredMember && (
        <LeadershipModal
          member={hoveredMember}
          onClose={() => setHoveredMember(null)}
        />
      )}

      <Footer />
    </>
  );
}

function LeadershipModal({ member, onClose }) {
  const fullName = member?.title?.rendered || "";
  const jobTitle = member?.acf?.job_title || "";
  const bioContent = member?.content?.rendered || "";
  const education = member?.acf?.education || "";
  const memberMedia = member?._embedded?.["wp:featuredmedia"]?.[0];

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .modal-scroll::-webkit-scrollbar { display: none; }
          .modal-scroll { scrollbar-width: none; -ms-overflow-style: none; }
        `,
        }}
      />

      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-2xl max-w-4xl max-h-[90vh] overflow-y-auto m-4 modal-scroll"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-8 md:p-12 bg-[#0070b9] relative">
            <button
              onClick={onClose}
              className="text-2xl font-light absolute top-2 right-3 px-4 py-2 text-white "
            >
              x
            </button>

            <div className="md:flex md:items-start md:justify-between">
              {memberMedia?.source_url && (
                <div className="md:w-1/3 lg:w-1/4 flex-shrink-0 pr-6">
                  <img
                    src={memberMedia.source_url}
                    alt={fullName}
                    className="w-40 h-40 object-cover rounded-full transition duration-700 ease-in-out transform hover:scale-110"
                  />
                </div>
              )}

              <div className="mb-6 md:mb-0 md:flex-1">
                <p className="font-medium text-[#58b947] mb-2 md:mt-0 mt-2">
                  Leadership Team
                </p>
                <h1 className="text-3xl md:text-4xl font-medium text-white">
                  {fullName}
                </h1>
                <h2 className="text-xl md:text-2xl text-white/80 mb-2">
                  {jobTitle}
                </h2>
              </div>
            </div>
          </div>

          <div className="p-8 md:p-12 text-[#0f243f]">
            <div className="max-w-4xl">
              <div
                className="prose prose-lg max-w-none mb-5"
                dangerouslySetInnerHTML={{
                  __html: bioContent?.replace(/<p.*?>/g, "<p class='mb-5'>"),
                }}
              />
              {education && (
                <>
                  <p className="font-semibold text-[#0070b9] mb-3">
                    Education & Qualifications
                  </p>
                  <p className="text-gray-700 whitespace-pre-line">
                    {education}
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="p-4 bg-gray-100 text-right">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-[#58b947] text-white rounded hover:bg-[#58b947]"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function LeadershipItem({ member, onClick }) {
  const fullName = member?.title?.rendered || "";
  const jobTitle = member?.acf?.job_title || "";
  const memberMedia = member?._embedded?.["wp:featuredmedia"]?.[0];

  return (
    <li className="w-full">
      <div
        className="flex items-center h-full cursor-pointer"
        onClick={() => onClick(member)}
      >
        {memberMedia?.source_url && (
          <div className="w-32 h-32 rounded-full overflow-hidden flex-shrink-0 mr-4">
            <img
              src={memberMedia.source_url}
              alt={fullName}
              className="h-full w-full object-cover transition duration-700 ease-in-out transform hover:scale-110"
            />
          </div>
        )}
        <div>
          <h3 className="md:text-xl text-lg font-semibold text-[#0070b9] mb-1">
            {fullName}
          </h3>
          <p className="text-base text-gray-600 font-medium">{jobTitle}</p>
        </div>
      </div>
    </li>
  );
}
